// v2/pages/admin/AdminQuoteDetailV2.js — Yonetim / Teklif detayi (/v2/admin/quotes/:no)
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, FileText, FileSpreadsheet, RotateCw, Search, X, ChevronLeft, ChevronRight,
  Loader2, Inbox, Store, Share2, EyeOff, Clock, Info, Trash2,
} from 'lucide-react';
import ToastStack from '../../components/Toast';
import useToast from '../../hooks/useToast';
import { Acilir, OzetKartlari, Tutarlar, Uyarilar, KalemSatiri } from '../../components/QuoteParts';
import { useAdminQuote, useAdminQuoteLines, adminQuoteApi } from '../../hooks/useAdminQuotes';
import { QUOTE_STATUS, LINE_STATUS, LINE_STATUS_ORDER, fmtTarih, kalanGun } from '../../hooks/useQuotes';
import { formatNumber } from '../../utils/format';
import { KaynakRozet, PaylasimRozet, SilModal } from './AdminQuotesV2';
import css from '../../styles/QuotesV2.module.css';
import a from '../../styles/AdminQuotesV2.module.css';
import '../../styles/tokens.css';

const PER_PAGE = [50, 100, 200];
const FILTRELER = ['all', ...LINE_STATUS_ORDER, 'kalkti'];
const CIKTI = [
  { id: 'all', label: 'Tüm kalemler' },
  { id: 'supplied', label: 'Yalnızca karşılanabilenler', hint: 'Tamamı + Kısmen' },
];

export default function AdminQuoteDetailV2() {
  const { no } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [sp, setSp] = useSearchParams();

  const [durum, setDurum] = useState(() => (FILTRELER.includes(sp.get('status')) ? sp.get('status') : 'all'));
  const [ara, setAra] = useState(sp.get('q') || '');
  const [araG, setAraG] = useState(ara);
  const [page, setPage] = useState(() => Math.max(parseInt(sp.get('page'), 10) || 1, 1));
  const [per, setPer] = useState(PER_PAGE[0]);
  const [indiriliyor, setIndiriliyor] = useState(null);
  const [cozumleniyor, setCozumleniyor] = useState(null);
  const [paylasiliyor, setPaylasiliyor] = useState(false);
  const [yenileniyor, setYenileniyor] = useState(false);
  const [silOnay, setSilOnay] = useState(false);
  const tabloRef = useRef(null);

  const teklif = useAdminQuote(no);
  const q = teklif.quote;
  const kalemler = useAdminQuoteLines(no, { status: durum, q: araG, page, per });

  useEffect(() => {
    const t = setTimeout(() => { setAraG(ara.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [ara]);

  useEffect(() => {
    const p = new URLSearchParams();
    if (durum !== 'all') p.set('status', durum);
    if (araG) p.set('q', araG);
    if (page > 1) p.set('page', String(page));
    if (p.toString() !== sp.toString()) setSp(p, { replace: true });
  }, [durum, araG, page, sp, setSp]);

  const filtreSec = useCallback((id) => {
    setDurum(id);
    setPage(1);
    if (tabloRef.current) tabloRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const indir = async (tur, scope) => {
    setIndiriliyor(tur);
    if (q && q.lineCount > 3000) toast.push('Büyük tekliflerde dosya 30 saniyeye kadar hazırlanabilir', 'info');
    try {
      await adminQuoteApi.download(no, tur, scope);
    } catch (e) {
      toast.push(e.message || 'Dosya indirilemedi', 'error');
    } finally {
      setIndiriliyor(null);
    }
  };

  const paylas = async () => {
    const acik = !q.shared;
    if (acik && !window.confirm(`${q.no} müşterinin (${q.cari.title || q.cari.code}) panelindeki "Tekliflerim" listesine eklenecek. Müşteri teklifi görüp sepete aktarabilecek. Devam edilsin mi?`)) return;
    setPaylasiliyor(true);
    try {
      const v = await adminQuoteApi.share(no, acik);
      teklif.setQuote((eski) => ({ ...eski, ...v.quote, warnings: eski ? eski.warnings : [] }));
      toast.push(acik ? 'Teklif müşteri paneline gönderildi' : 'Teklif müşteriye kapatıldı', 'success');
    } catch (e) {
      toast.push(e.message || 'İşlem yapılamadı', 'error');
    } finally {
      setPaylasiliyor(false);
    }
  };

  const yenile = async () => {
    setYenileniyor(true);
    try {
      const v = await adminQuoteApi.renew(no);
      toast.push(`Güncel fiyatlarla ${v.quote.no} oluşturuldu`, 'success');
      navigate(`/v2/admin/quotes/${v.quote.no}`);
    } catch (e) {
      toast.push(e.message || 'Teklif yenilenemedi', 'error');
    } finally {
      setYenileniyor(false);
    }
  };

  const cozumle = async (l, kod) => {
    setCozumleniyor(`${l.id}:${kod}`);
    try {
      const v = await adminQuoteApi.resolve(no, l.id, kod);
      kalemler.kalemGuncelle(v.line);
      teklif.setQuote((eski) => ({ ...eski, ...v.quote, warnings: eski ? eski.warnings : [] }));
      toast.push(`${kod} seçildi`, 'success');
    } catch (e) {
      toast.push(e.message || 'Kalem güncellenemedi', 'error');
    } finally {
      setCozumleniyor(null);
    }
  };

  if (teklif.loading && !q) {
    return <div className={a.page}><div className={css.skelList}>{[0, 1, 2].map((i) => <div key={i} className={css.skel} />)}</div></div>;
  }
  if (teklif.error || !q) {
    return (
      <div className={a.page}>
        <div className={css.empty}>
          <Inbox size={34} strokeWidth={1.3} />
          <span>{teklif.error || 'Teklif bulunamadı'}</span>
          <Link to="/v2/admin/quotes" className={css.btnGhost}><ArrowLeft size={14} /> Tekliflere dön</Link>
        </div>
      </div>
    );
  }

  const st = QUOTE_STATUS[q.status] || QUOTE_STATUS['Hazır'];
  const kalan = kalanGun(q.validUntil);

  return (
    <div className={a.page}>
      <ToastStack items={toast.items} onDismiss={toast.dismiss} />

      <div className={a.head}>
        <div>
          <h2 className={a.title}>
            <Link to="/v2/admin/quotes" className={css.back} aria-label="Teklifler"><ArrowLeft size={18} /></Link>
            {q.no}
            <span className={`${css.pill} ${css[st.tone]}`}>{st.label}</span>
            <KaynakRozet q={q} />
            <PaylasimRozet q={q} />
          </h2>
          <p className={a.sub}>
            {fmtTarih(q.createdAt)} · {q.source === 'admin' ? `Hazırlayan: ${q.createdBy || '—'}` : `Müşteri kullanıcısı: ${q.createdBy || '—'}`}
            {q.renewedFrom && <> · <Link to={`/v2/admin/quotes/${q.renewedFrom}`} className={css.quoteNo}>{q.renewedFrom}</Link> yenilemesi</>}
          </p>
        </div>
        <span className={a.headActions}>
          <Acilir etiket="PDF" Icon={FileText} mesgul={indiriliyor === 'pdf'} secenekler={CIKTI}
                  onSec={(s) => indir('pdf', s)} />
          <Acilir etiket="Excel" Icon={FileSpreadsheet} mesgul={indiriliyor === 'excel'} secenekler={CIKTI}
                  onSec={(s) => indir('excel', s)} />
          {q.source === 'admin' && (
            <button type="button" className={q.shared ? css.btnGhost : css.btnPrimary} onClick={paylas}
                    disabled={paylasiliyor || (q.shared && ['Sepete Aktarıldı', 'Siparişe Dönüştü'].includes(q.status))}>
              {paylasiliyor ? <Loader2 size={14} className={css.spin} /> : (q.shared ? <EyeOff size={14} /> : <Share2 size={14} />)}
              {q.shared ? 'Müşteriye Kapat' : 'Müşteri Paneline Gönder'}
            </button>
          )}
          <button type="button" className={css.btnGhost} onClick={yenile} disabled={yenileniyor}
                  title="Aynı kalemlerle güncel fiyat ve stokla yeni teklif">
            {yenileniyor ? <Loader2 size={14} className={css.spin} /> : <RotateCw size={14} />}
            Güncel Fiyatla Yenile
          </button>
          <button type="button" className={a.btnDangerGhost} onClick={() => setSilOnay(true)}>
            <Trash2 size={14} /> Sil
          </button>
        </span>
      </div>

      <div className={a.infoGrid}>
        <section className={`${css.card} ${a.cariCard}`}>
          <span className={a.cariIcon}><Store size={20} /></span>
          <span className={a.cariBody}>
            <span className={a.cariTitle}>{q.cari.title || '—'}</span>
            <span className={a.cariCode}>Cari kodu: <b>{q.cari.code}</b></span>
          </span>
          <Link to={`/v2/admin/customers/${encodeURIComponent(q.cari.code)}`} className={css.btnSmall}>Cari kartı</Link>
        </section>
        <section className={`${css.card} ${a.metaCard}`}>
          <span className={a.meta}><span>Geçerlilik</span><b>{fmtTarih(q.validUntil, false)}
            {q.expired ? <span className={`${css.daysLeft} ${css.daysWarn}`}>doldu</span>
              : kalan !== null && <span className={css.daysLeft}>{kalan <= 0 ? 'bugün' : `${kalan} gün`}</span>}</b></span>
          <span className={a.meta}><span>Euro kuru</span><b>1 € = {formatNumber(q.euroRate, 4)} ₺</b></span>
          <span className={a.meta}><span>Kaynak dosya</span><b>{q.fileName || '—'}</b></span>
          {q.note && <span className={a.meta}><span>Not</span><b>{q.note}</b></span>}
          {q.transferredAt && <span className={a.meta}><span>Sepete aktarım</span><b>{fmtTarih(q.transferredAt)}</b></span>}
          {q.orderedAt && <span className={a.meta}><span>Sipariş</span><b>{fmtTarih(q.orderedAt)}</b></span>}
        </section>
      </div>

      {q.source === 'admin' && !q.shared && (
        <div className={css.validBar}>
          <Info size={15} />
          <span>Bu teklif müşteriye kapalı. PDF/Excel'i indirip iletebilir ya da <b>Müşteri Paneline Gönder</b> ile müşterinin Tekliflerim listesine ekleyebilirsiniz.</span>
        </div>
      )}
      {q.expired && (
        <div className={`${css.validBar} ${css.validExpired}`}>
          <Clock size={15} />
          <span>Geçerlilik süresi doldu. Güncel fiyatlarla yeni teklif için <b>Güncel Fiyatla Yenile</b>'yi kullanın.</span>
        </div>
      )}

      <OzetKartlari q={q} aktif={durum} onSec={filtreSec} />
      <Tutarlar q={q} />
      <Uyarilar q={q} />

      {silOnay && (
        <SilModal
          teklifler={[q]}
          onKapat={() => setSilOnay(false)}
          onSilindi={() => navigate('/v2/admin/quotes', { replace: true })}
        />
      )}

      <section className={css.card} ref={tabloRef}>
        <div className={css.toolbar}>
          <span className={css.searchBox}>
            <Search size={14} className={css.searchIcon} />
            <input className={css.searchInput} value={ara} onChange={(e) => setAra(e.target.value)}
                   placeholder="Kod, ürün adı veya müşteri stok kodu…" aria-label="Kalemlerde ara" />
            {ara && (
              <button type="button" className={css.searchClear} onClick={() => setAra('')} aria-label="Temizle">
                <X size={12} />
              </button>
            )}
          </span>
          <span className={css.tabs} role="tablist">
            {FILTRELER.map((id) => {
              const etiket = id === 'all' ? 'Tümü' : id === 'kalkti' ? 'Kalkan' : LINE_STATUS[id].short;
              const n = id === 'all' ? q.lineCount : ((q.counts && q.counts[id]) || 0);
              if (id === 'kalkti' && !n) return null;
              return (
                <button key={id} type="button" role="tab" aria-selected={durum === id}
                        className={`${css.tab} ${durum === id ? css.tabOn : ''}`} onClick={() => filtreSec(id)}>
                  {etiket}<span className={css.tabCount}>{formatNumber(n)}</span>
                </button>
              );
            })}
          </span>
        </div>

        {kalemler.error && <div className={css.alertRed}>{kalemler.error}</div>}

        {kalemler.loading ? (
          <div className={css.skelList}>{[0, 1, 2, 3].map((i) => <div key={i} className={css.skel} />)}</div>
        ) : kalemler.lines.length === 0 ? (
          <div className={css.empty}><Inbox size={30} strokeWidth={1.3} /><span>Bu filtrede kalem bulunmuyor.</span></div>
        ) : (
          <div className={`${css.tableScroll} ${kalemler.refreshing ? css.fading : ''}`}>
            <table className={`${css.table} ${css.lTable}`}>
              <thead>
                <tr>
                  <th aria-label="Görsel" />
                  <th>Ürün Kodu</th>
                  <th>Ürün Açıklaması</th>
                  <th className={css.right}>Talep</th>
                  <th>Karşılanan</th>
                  <th>Durum</th>
                  <th>Canlı Stok</th>
                  <th className={css.right}>Birim Brüt</th>
                  <th className={css.center}>İndirim</th>
                  <th className={css.right}>Birim Net</th>
                  <th className={css.right}>Toplam Tutar</th>
                </tr>
              </thead>
              <tbody>
                {kalemler.lines.map((l) => (
                  <KalemSatiri key={l.id} l={l} secimli={false} yonetim onCozumle={cozumle}
                               cozumleniyor={cozumleniyor} kilitli={q.expired} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {kalemler.totalItems > 0 && (
          <div className={css.pager}>
            <span className={css.pagerInfo}><b>{formatNumber(kalemler.totalItems)}</b> kalem · Sayfa {page}/{kalemler.totalPages}</span>
            <label className={css.perLabel}>
              Sayfa başına
              <select className={css.select} value={per} onChange={(e) => { setPer(Number(e.target.value)); setPage(1); }}>
                {PER_PAGE.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
            <span className={css.spacer} />
            <button type="button" className={css.pageBtn} disabled={page <= 1} onClick={() => setPage((p) => p - 1)} aria-label="Önceki sayfa">
              <ChevronLeft size={15} />
            </button>
            <button type="button" className={css.pageBtn} disabled={page >= kalemler.totalPages} onClick={() => setPage((p) => p + 1)} aria-label="Sonraki sayfa">
              <ChevronRight size={15} />
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
